import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { useFocusedMeetingAPI, useMeetingSelector } from "../../../../hooks/meeting";
import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import React, { useEffect, useMemo, useState } from "react";
import { useStudentContext } from "../context";

import { HoverableButton, ReactIcons, Text } from "@kalyzee/escaet-ui";

import { MeetingSession, MeetingSessionHighlightingState, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { MeetingPlayerTriLayoutContainer } from "../../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import "./dashboard.css";
import styles from "./dashboard.module.css";
import { useVideoSubscriptions } from "../../../../hooks/ovenmedia";
import { MasterPlayerWorkBox } from "../../../../components/workBoxes/MasterPlayerWorkBox";
import { useBoardMedia } from "../../../../hooks/board";
import { pushDeviceControlsCmd } from "../../../../hooks/deviceControls";
import { isExpert } from "../../../../helpers/roles";

export const MeetingDashboardStudentPage = () => {
  const studentCtx = useStudentContext();

  const meetingAPI = useFocusedMeetingAPI();
  const [meeting] = useMeetingSelector(meetingAPI, (state) => state.meeting);
  const [ownSession] = useMeetingSelector(meetingAPI, (state) => state.session);

  const [focusedVideoSessionId, setFocusedVideoSessionId] = useState<string>();

  const subscribptionsAPI = useVideoSubscriptions();

  const boardLocalOrRemoteMedia = useBoardMedia(meetingAPI);

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!studentCtx || !meeting) return { enabledMedia: [] };
    const studentMeetingOptions = studentCtx.getMeetingOptions(meeting.id);

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));
    
    return { enabledMedia };
  }, [meeting, boardLocalOrRemoteMedia, studentCtx]);

  const playerItems = useMemo(() => enabledMedia?.map(
    (media) =>
      new PlayerItem(media.type === "local" ? {
        type: "stream",
        mediaStream: media.stream,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      } : {
        type: "video_session",
        videoSession: media.videoSession,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      })
  ), [enabledMedia]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const remoteVideoSessions = enabledMedia
      ?.filter((media) => media.type === "remote")
      .map((media) => (media as any).videoSession);
    subscribptionsAPI.update(remoteVideoSessions.map((media) => ({ session: media, active: true })));
    return () => subscribptionsAPI.update(remoteVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledMedia]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!studentCtx) return null;
  if (!meeting) return null;
  if (!meetingAPI) return null;

  const { layout: layoutIndex } = studentCtx;

  const renderPlayerHeader = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, focusMode: boolean) => {
    const meetingSession = session.data.session;
    const isPedagogy = meetingSession.extra?.isPedagogy;
    const username = meetingSession.user?.username ?? "";
    const label = session.data.mediaSession.extra?.label ?? username;
    let text = label;
    if (isPedagogy) {
      text = `${label} (Pédagogie)`;
    } else if (meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING) {
      text = `${label} (Au tableau)`;
    }
    const renderUsername = () => (
      <Text
        style={{
          fontWeight: 800,
          fontSize: focusMode ? "1.2em" : "0.8em",
          backgroundColor: isPedagogy ? "#f9af5c" : "white",
          borderRadius: "0.25em",
          padding: "0.25em 0.5em",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        value={text}
      />
    );

    const renderFullScreenButton = () =>
      focusedVideoSessionId ? (
        <ReactIcons.CloseIconBlackBg onClick={() => setFocusedVideoSessionId(undefined)} style={{ width: "2em", height: "2em", cursor: "pointer" }} />
      ) : (
        <HoverableButton.FullScreen onClick={() => setFocusedVideoSessionId(session.getId())} style={{ width: "2em", height: "2em" }} />
      );

    if (focusMode) {
      return (
        <div className={styles.playerHeaderContainer} style={{ backgroundColor: "white" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                color: "white",
                fontWeight: 800,
                backgroundColor: "#05C496",
                padding: "0.5em",
                borderRadius: "0.25em",
              }}
              value={"Mode focus".toUpperCase()}
            />
            {renderUsername()}
          </div>
          {renderFullScreenButton()}
        </div>
      );
    }

    return (
      <div className={styles.playerHeaderContainer}>
        {renderUsername()}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "1em",
          }}
        >
          {renderFullScreenButton()}
        </div>
      </div>
    );
  };



  const renderPlayer = (
    rendererProvider: () => React.ReactNode,
    session: MeetingSession,
    mediaSession: MeetingSessionMedia,
    isSmall: boolean,
  ) => {
    const hasControls = !!mediaSession.extra?.controls && isExpert(ownSession);
    const deviceId = mediaSession.deviceId;
    const roomId = meeting.room?.id;
    return (
      <MasterPlayerWorkBox
        meetingAPI={meetingAPI}
        session={session}
        mediaSession={mediaSession}
        overlayMode={"normal"}
        controls={hasControls ? {
          onUp: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "move" : "stopMove", { direction: "up" });
          },
          onDown: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "move" : "stopMove", { direction: "down" });
          },
          onLeft: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "move" : "stopMove", { direction: "left" });
          },
          onRight: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "move" : "stopMove", { direction: "right" });
          },
          onZoomIn: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "zoom" : "stopZoom", { direction: "in" });
          },
          onZoomOut: (on: boolean) => {
            if (!roomId || !deviceId) return;
            pushDeviceControlsCmd(roomId, deviceId, on ? "zoom" : "stopZoom", { direction: "out" });
          },
        } : undefined}
        isSmall={isSmall}
        mediaControls
      >
        {rendererProvider()}
      </MasterPlayerWorkBox>
    );
  };

  const renderNewLayoutContent = () => {
    if (!playerItems?.length) {
      return (
        <div style={{ 
          width: "100%", 
          height: "100%", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          border: "2px dashed var(--color-element-text)",
          borderRadius: 20,
        }}>
          <div style={{ color: "var(--color-element-text)" }}>{boardLocalOrRemoteMedia.length ? "Aucun flux sélectionné" : "Aucun flux réceptionné"}</div>
        </div>
      );
    }
    return (
      <MeetingPlayerTriLayoutContainer
        meeting={meeting}
        items={playerItems}
        layoutProps={{ square: { noPages: true } }}
        renderItem={(rendererProvider, item, isSelectedPlayer) => {
          const itemData = item.getData();
          // if (itemData.type !== "video_session") return rendererProvider(); // should not happen
          // console.log("selected player : ", isSelectedPlayer)
          const session = itemData.extra?.meetingSession;
          const mediaSession = itemData.extra?.mediaSession;
          if (!session || !mediaSession) return undefined;
          return renderPlayer(rendererProvider, session, mediaSession, !isSelectedPlayer);
        }}
        layout={layoutIndex}
        autoOnOffVideo={false}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {/* {renderFocusContent()} */}
    </div>
  );
};
